// src/context/AppDataContext.js
import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
} from "react";
import { useAuth } from "./AuthContext";
import api from "../services/api";
import { getCartCount } from "../utils/cart";

const AppDataContext = createContext(null);

export const AppDataProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [favoritesCount, setFavoritesCount] = useState(0);
  const [cartCount, setCartCount] = useState(getCartCount());

  // This function will be called to refresh all global data points.
  const fetchData = useCallback(async () => {
    if (isAuthenticated) {
      console.log("🔄 Refreshing app data (counts)...");
      try {
        // Fetch all data points in parallel for performance
        const [notifRes, favRes, convosRes] = await Promise.all([
          api.get("/notifications/"),
          api.get("/favorites/"),
          api.get("/conversations/"), // Fetch all conversations
        ]);

        // Calculate unread notifications
        setUnreadNotifications(
          (notifRes.data.results || notifRes.data).filter((n) => !n.is_read)
            .length
        );

        // Set favorites count
        setFavoritesCount((favRes.data.results || favRes.data).length);

        // Calculate total unread messages from all conversations
        const totalUnreadMsgs = (
          convosRes.data.results || convosRes.data
        ).reduce((total, convo) => {
          return total + (convo.unread_messages_count || 0);
        }, 0);
        setUnreadMessages(totalUnreadMsgs);
        console.log(
          `✅ App data updated: ${totalUnreadMsgs} unread msgs, ${unreadNotifications} unread notifs, ${favoritesCount} favs`
        );
      } catch (error) {
        console.error("❌ Failed to fetch global app data", error);
      }
    }
  }, [isAuthenticated, unreadNotifications, favoritesCount]); // dependencies ensure we have latest values

  useEffect(() => {
    const syncCart = () => setCartCount(getCartCount());
    window.addEventListener("chafadi-cart-updated", syncCart);
    window.addEventListener("storage", syncCart);
    syncCart();
    return () => {
      window.removeEventListener("chafadi-cart-updated", syncCart);
      window.removeEventListener("storage", syncCart);
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
      // Poll for new data periodically (e.g., every minute)
      const interval = setInterval(fetchData, 60000);
      return () => clearInterval(interval);
    } else {
      // If user logs out, reset all counts to zero
      setUnreadMessages(0);
      setUnreadNotifications(0);
      setFavoritesCount(0);
    }
  }, [isAuthenticated, fetchData]);

  const value = {
    unreadMessages,
    unreadNotifications,
    favoritesCount,
    cartCount,
    refreshData: fetchData,
  };

  return (
    <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>
  );
};

export const useAppData = () => {
  return useContext(AppDataContext);
};
